"use client";

import { useEffect } from "react";
import { ArrowLeftIcon, RotateCwIcon, TriangleAlertIcon } from "lucide-react";

import { Button } from "@tripcart/traveler-ui/components/button";

import { ButtonLink } from "@/components/button-link";
import { type Translator, useTranslator } from "@/lib/translation-context";

import styles from "./error.module.css";

/** Tenant-tinted top wash. Mirrors `NotFoundContent` so the two failure
 *  surfaces read as one family under any agency palette. */
function BrandWash(): React.ReactElement {
  return <div aria-hidden className={styles.brandWash} />;
}

/** Dot-grid floor, fading upward. Same treatment as the 404 surface. */
function DotGridFloor(): React.ReactElement {
  return <div aria-hidden className={styles.dotGridFloor} />;
}

/** Static tinted disc. Carries no keyframes on purpose — the 404's character
 *  floats and wanders because being lost is playful; a failure is not. */
function ErrorMark(): React.ReactElement {
  return (
    <div aria-hidden className={styles.mark}>
      <TriangleAlertIcon className={styles.markIcon} strokeWidth={1.5} />
    </div>
  );
}

function ErrorActions({
  t,
  reset,
}: {
  t: Translator;
  reset: () => void;
}): React.ReactElement {
  return (
    <div className={styles.actions}>
      <Button size="lg" onClick={reset}>
        <RotateCwIcon strokeWidth={2} />
        {t("Try Again")}
      </Button>
      <ButtonLink href="/" variant="secondary" size="lg">
        <ArrowLeftIcon strokeWidth={2} />
        {t("Back to Home")}
      </ButtonLink>
    </div>
  );
}

/**
 * Public storefront error surface — the calm sibling of `NotFoundContent`.
 *
 * Renders INSIDE the `(public)` layout, so `<SiteChrome>` (header + footer)
 * and the per-tenant branding survive the failure; the root `app/error.tsx`
 * sits outside all of that and is now only the fallback for a layout-level
 * throw. That difference is the point: a visitor hitting a transient API
 * error should still land on the agency's site, not a bare page.
 *
 * Copy states only what is known. It does not promise the problem is
 * temporary or that a retry will succeed — `reset()` is offered as an
 * action, not a prediction. "It isn't a missing page" is accurate by
 * construction: a genuine 4xx routes to `notFound()` before it can reach an
 * error boundary (see `lib/unwrap-api-result.ts`), and an unmatched URL
 * renders not-found rather than this.
 */
export default function PublicError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): React.ReactElement {
  const t = useTranslator();

  useEffect(() => {
    // Literal process.env.NODE_ENV (Next inlines it) avoids importing
    // @/env.client, which pulls all of zod into this client chunk.
    // eslint-disable-next-line no-restricted-properties -- the suggested fix (import the validated env) is exactly the zod pull we're avoiding
    if (process.env.NODE_ENV !== "production") {
      console.error(error);
    }
  }, [error]);

  return (
    <section
      aria-labelledby="storefront-error-heading"
      className={styles.section}
    >
      <BrandWash />

      <div className={styles.content}>
        <ErrorMark />

        <div className={styles.body}>
          <h1 id="storefront-error-heading" className={styles.heading}>
            {t("This Page Didn't Load")}
          </h1>
          <p className={styles.lead}>
            {t(
              "Something went wrong on our end while loading this page. It isn't a missing page — the address is correct.",
            )}
          </p>

          <ErrorActions t={t} reset={reset} />

          {/* Support handle. Rendered only when Next produced one, so the page
              never shows an empty "Reference:" label. */}
          {error.digest ? (
            <p className={styles.reference}>
              {t("Reference")}: {error.digest}
            </p>
          ) : null}
        </div>
      </div>

      <DotGridFloor />
    </section>
  );
}
